import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, FileText, FileSpreadsheet, FileJson } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export function AnalyticsExport({ analytics }: { analytics: any }) {
  const { toast } = useToast();
  const { user } = useAuth();

  const exportAsCSV = () => {
    try {
      const csv = convertToCSV(analytics);
      downloadFile(csv, 'analytics.csv', 'text/csv');
      toast({ title: "Exported successfully", description: "Analytics data exported as CSV" });
    } catch {
      toast({ title: "Export failed", description: "Failed to export analytics", variant: "destructive" });
    }
  };

  const exportAsJSON = () => {
    try {
      const json = JSON.stringify(analytics, null, 2);
      downloadFile(json, 'analytics.json', 'application/json');
      toast({ title: "Exported successfully", description: "Analytics data exported as JSON" });
    } catch {
      toast({ title: "Export failed", description: "Failed to export analytics", variant: "destructive" });
    }
  };

  const exportAsPDF = () => {
    try {
      generatePDFReport(analytics, user);
      toast({ title: "Report generated", description: "Your performance report has been downloaded" });
    } catch (e) {
      console.error(e);
      toast({ title: "Export failed", description: "Failed to generate PDF report", variant: "destructive" });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportAsPDF}>
          <FileText className="h-4 w-4 mr-2" />
          Performance Report (PDF)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportAsCSV}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Data (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportAsJSON}>
          <FileJson className="h-4 w-4 mr-2" />
          Raw (JSON)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function convertToCSV(data: any): string {
  if (!data) return '';
  const headers = ['Metric', 'Value'];
  const rows = [
    ['Total Study Time (hours)', Math.floor((data.totalStudyTimeSeconds || 0) / 3600)],
    ['Exam Readiness Score', data.examReadinessScore || 0],
    ['Topics Mastered', data.topicsMastered || 0],
    ['Current Streak (days)', data.currentStreak || 0],
    ['Longest Streak (days)', data.longestStreak || 0],
  ];
  return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}

function downloadFile(content: string, filename: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function generatePDFReport(analytics: any, user: any) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  // Brand header bar
  doc.setFillColor(13, 148, 136); // medical teal
  doc.rect(0, 0, pageWidth, 60, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('QuickMedicalReview', margin, 28);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Personal Performance Report', margin, 46);

  y = 90;

  // User & date
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  const userName = user?.user_metadata?.full_name || user?.email || 'Student';
  const today = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Student: ${userName}`, margin, y);
  doc.text(`Generated: ${today}`, pageWidth - margin, y, { align: 'right' });
  y += 24;

  // Summary section
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(13, 148, 136);
  doc.text('Summary', margin, y);
  y += 6;
  doc.setDrawColor(13, 148, 136);
  doc.line(margin, y, pageWidth - margin, y);
  y += 14;

  const totalHours = ((analytics?.totalStudyTimeSeconds || 0) / 3600).toFixed(1);
  const summaryRows = [
    ['Exam Readiness', `${analytics?.examReadinessScore || 0} / 100`],
    ['Total Questions Attempted', String(analytics?.totalAttempts || 0)],
    ['Overall Accuracy', `${analytics?.overallAccuracy || 0}%`],
    ['Total Study Time', `${totalHours} hours`],
    ['Topics Mastered', String(analytics?.topicsMastered || 0)],
    ['Current Streak', `${analytics?.currentStreak || 0} days`],
    ['Longest Streak', `${analytics?.longestStreak || 0} days`],
  ];

  autoTable(doc, {
    startY: y,
    head: [['Metric', 'Value']],
    body: summaryRows,
    theme: 'grid',
    headStyles: { fillColor: [13, 148, 136], textColor: 255 },
    margin: { left: margin, right: margin },
    styles: { fontSize: 10, cellPadding: 6 },
  });
  y = (doc as any).lastAutoTable.finalY + 24;

  // Topic mastery
  const topics = (analytics?.topicMastery || []).slice(0, 15);
  if (topics.length > 0) {
    if (y > 700) { doc.addPage(); y = margin; }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(13, 148, 136);
    doc.text('Topic Mastery', margin, y);
    y += 6;
    doc.line(margin, y, pageWidth - margin, y);
    y += 14;

    autoTable(doc, {
      startY: y,
      head: [['Topic', 'Attempts', 'Accuracy', 'Mastery']],
      body: topics.map((t: any) => [
        String(t.topic_name || t.name || 'Unknown'),
        String(t.attempts || 0),
        `${Math.round(t.accuracy || 0)}%`,
        labelMastery(t.accuracy || 0),
      ]),
      theme: 'striped',
      headStyles: { fillColor: [13, 148, 136], textColor: 255 },
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 5 },
    });
    y = (doc as any).lastAutoTable.finalY + 24;
  }

  // Weak areas
  const weak = (analytics?.weakAreas || []).slice(0, 10);
  if (weak.length > 0) {
    if (y > 700) { doc.addPage(); y = margin; }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text('Areas Needing Attention', margin, y);
    y += 6;
    doc.setDrawColor(220, 38, 38);
    doc.line(margin, y, pageWidth - margin, y);
    y += 14;

    autoTable(doc, {
      startY: y,
      head: [['Topic', 'Accuracy', 'Recommendation']],
      body: weak.map((w: any) => [
        String(w.topic_name || w.name || 'Unknown'),
        `${Math.round(w.accuracy || 0)}%`,
        'Practice more questions',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38], textColor: 255 },
      margin: { left: margin, right: margin },
      styles: { fontSize: 9, cellPadding: 5 },
    });
    y = (doc as any).lastAutoTable.finalY + 24;
  }

  // Footer on every page
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `QuickMedicalReview • Personal Performance Report • Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 20,
      { align: 'center' }
    );
  }

  const filename = `performance-report-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
}

function labelMastery(acc: number): string {
  if (acc >= 85) return 'Mastered';
  if (acc >= 70) return 'Proficient';
  if (acc >= 50) return 'Developing';
  return 'Needs Work';
}
